import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { EyeClosed, Eye } from 'lucide-react';
import { AtSign, Lock } from 'react-feather';
import { AnimatePresence, motion } from 'framer-motion';

import { Button } from 'components/Elements';
import { Form, InputField } from 'components/Form';
import storage from 'utils/storage';
import { passwordLogin } from '../api/passwordLogin';

const REMEMBER_DURATION_DAYS = 7;

export const PasswordLoginForm = () => {
  const navigate = useNavigate();
  const fcmToken = storage.getFCMToken();

  const [isLoading, setIsLoading] = useState(false);
  const [sessionTimeoutMessage, setSessionTimeoutMessage] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [emailEntered, setEmailEntered] = useState(false);
  const [showLoginButton, setShowLoginButton] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('sessionTimeout') === 'true') {
      setSessionTimeoutMessage(
        'You have been logged out due to inactivity. Please log in again.'
      );
    }
  }, []);

  const togglePasswordVisibility = () => {
    setPasswordVisible((prev) => !prev);
  };

  const handleFormSubmit = async ({ email, password }) => {
    try {
      setIsLoading(true);
      const result = await passwordLogin({ email, password, fcmToken });

      storage.setTempData({
        user: {
          allow2FA: result.allow2FA,
          allowEmailOTPLogin: result.allowEmailOTPLogin,
          allowAuthenticator: result.allowAuthenticator,
          allowSMS: result.allowSMSCodeVerification,
          email: result.email,
          phoneNumber: result.phoneNumber,
        },
        token: result.token,
      });

      Cookies.set('email', result.email);
      Cookies.set('phone_number', result.phoneNumber);
      Cookies.set('authentication_level_password', 'pass');

      if (rememberMe) {
        Cookies.set('remembered_password', password, {
          expires: REMEMBER_DURATION_DAYS,
        });
      } else {
        Cookies.remove('remembered_password');
      }

      if (!result.allowAuthenticator) {
        navigate('/auth/security/setup-required');
      } else if (!result.allowEmailOTPLogin) {
        navigate('/auth/security/email/setup');
      } else if (!result.allowSMSCodeVerification) {
        navigate('/auth/security/sms/setup');
      } else {
        navigate('/auth/security/authenticator');
      }
    } catch (error) {
      setIsLoading(false);
      // handle error
    }
  };

  return (
    <div>
      {sessionTimeoutMessage && (
        <div className="bg-red-100 border border-red-300 text-sm text-red-800 p-4 mb-4 rounded-md text-center dark:bg-red-800/20 dark:text-red-200">
          {sessionTimeoutMessage}
        </div>
      )}

      <Form onSubmit={handleFormSubmit}>
        {({ register, watch, formState }) => {
          const email = watch('email');
          const password = watch('password');
          const isValidEmail = /\S+@\S+\.\S+/.test(email || '');

          useEffect(() => {
            setEmailEntered(isValidEmail);
          }, [isValidEmail]);

          useEffect(() => {
            if (!emailEntered || !password) {
              setShowLoginButton(false);
              return;
            }

            if (typingTimeout) clearTimeout(typingTimeout);

            const timeout = setTimeout(() => {
              setShowLoginButton(true);
            }, 500);

            setTypingTimeout(timeout);
          }, [password]);

          return (
            <>
              <InputField
                label="Email"
                type="email"
                startIcon={<AtSign />}
                variant="filled"
                error={formState.errors['email']}
                registration={register('email')}
              />

              <AnimatePresence>
                {emailEntered && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="mt-4"
                  >
                    <InputField
                      label="Password"
                      type={passwordVisible ? 'text' : 'password'}
                      startIcon={<Lock />}
                      variant="filled"
                      error={formState.errors['password']}
                      registration={register('password')}
                      endIcon={
                        <button
                          type="button"
                          onClick={togglePasswordVisibility}
                          aria-label="Toggle Password Visibility"
                        >
                          {passwordVisible ? <Eye /> : <EyeClosed />}
                        </button>
                      }
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              {emailEntered && (
                <div className="mt-3 flex items-center justify-between text-sm text-white">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="bg-white"
                    />
                    <span>Remember me</span>
                  </label>
                </div>
              )}

              <AnimatePresence>
                {showLoginButton && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Button
                      type="submit"
                      className="w-full mt-5 glass-button"
                      isLoading={isLoading}
                    >
                      Sign In
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          );
        }}
      </Form>
    </div>
  );
};
